/**
 * Utility functions for converting between comma-separated strings and repeater arrays
 * Used by the stringRepeater field type in wizards
 */

export interface RepeaterItem {
  value: string;
}

/**
 * Parses a delimited string into an array of repeater items
 * Handles multiple delimiters: commas, semicolons, and newlines by default
 *
 * @param stringValue - The string containing delimited values
 * @param delimiter - Custom delimiter (optional, defaults to comma/semicolon/newline)
 * @returns Array of repeater items with value property
 *
 * @example
 * parseStringToRepeaterArray("Pool, Gym; Garden\nRoof Access")
 * // Returns: [{value: "Pool"}, {value: "Gym"}, {value: "Garden"}, {value: "Roof Access"}]
 */
export const parseStringToRepeaterArray = (
  stringValue: string | null | undefined,
  delimiter?: string | null
): RepeaterItem[] => {
  if (!stringValue || typeof stringValue !== "string") {
    return [];
  }

  // Use custom delimiter or default to comma, semicolon, or newline
  const delimiterPattern = delimiter
    ? new RegExp(delimiter.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g")
    : /[,;\n]+/;

  return stringValue
    .split(delimiterPattern)
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .map((value) => ({ value }));
};

/**
 * Converts an array of repeater items back to a delimited string
 * Filters out empty values and joins with the specified delimiter
 *
 * @param repeaterArray - Array of repeater items
 * @param delimiter - Delimiter to use (defaults to ", ")
 * @returns Delimited string
 *
 * @example
 * joinRepeaterArrayToString([{value: "Pool"}, {value: "Gym"}, {value: "Garden"}])
 * // Returns: "Pool, Gym, Garden"
 */
export const joinRepeaterArrayToString = (
  repeaterArray: RepeaterItem[] | undefined,
  delimiter: string | null | undefined = ", "
): string => {
  if (!Array.isArray(repeaterArray)) {
    return "";
  }

  const actualDelimiter = delimiter || ", ";

  return repeaterArray
    .filter((item) => item && item.value && item.value.trim().length > 0)
    .map((item) => item.value.trim())
    .join(actualDelimiter);
};

/**
 * Ensures the repeater array has at least the minimum number of items
 * Adds empty items if needed
 *
 * @param repeaterArray - Current array of repeater items
 * @param minItems - Minimum number of items required
 * @returns Array with at least minItems elements
 */
export const ensureMinimumItems = (
  repeaterArray: RepeaterItem[],
  minItems: number
): RepeaterItem[] => {
  const currentLength = repeaterArray.length;

  if (currentLength >= minItems) {
    return repeaterArray;
  }

  const itemsToAdd = minItems - currentLength;
  const emptyItems = Array.from({ length: itemsToAdd }, () => ({ value: "" }));

  return [...repeaterArray, ...emptyItems];
};

/**
 * Truncates the repeater array to the maximum number of items
 *
 * @param repeaterArray - Current array of repeater items
 * @param maxItems - Maximum number of items allowed
 * @returns Array with at most maxItems elements
 */
export const enforceMaximumItems = (
  repeaterArray: RepeaterItem[],
  maxItems: number
): RepeaterItem[] => {
  if (repeaterArray.length <= maxItems) {
    return repeaterArray;
  }

  return repeaterArray.slice(0, maxItems);
};
